import React, { useEffect, useState } from 'react';
import api from '../services/api';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { useToast } from '../hooks/use-toast';
import { 
  DndContext, 
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { 
  Plus, 
  GripVertical, 
  Edit, 
  Trash2, 
  Eye, 
  EyeOff,
  Save,
  X,
  Upload,
  Image as ImageIcon,
  Search,
  Globe,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import ImageUpload from '../components/ImageUpload';

const SECTION_TYPES = [
  { value: 'hero', label: 'Hero Section' },
  { value: 'cta_strip', label: 'CTA Strip' },
  { value: 'slideshow', label: 'Slideshow/Carousel' },
  { value: 'feature_grid', label: 'Feature Grid' },
  { value: 'news', label: 'News (from Blog)' },
  { value: 'text', label: 'Text Section' },
  { value: 'image_text', label: 'Image + Text' },
  { value: 'video_text', label: 'Video + Text' },
  { value: 'embed', label: 'Embed (General)' },
  { value: 'gallery', label: 'Image Gallery' },
  { value: 'sponsors', label: 'Sponsors / Partners' },
  { value: 'press', label: 'Press / Media Coverage' },
  { value: 'newsletter_flodesk', label: 'Newsletter (Flodesk)' },
];

// Slides Editor Component
function SlidesEditor({ slides = [], onChange }) {
  const [editingIndex, setEditingIndex] = useState(null);

  const addSlide = () => {
    onChange([...slides, { id: slides.length + 1, title: '', subtitle: '', image: '', button_text: '', button_link: '' }]);
  };

  const updateSlide = (index, field, value) => {
    const updated = [...slides];
    updated[index] = { ...updated[index], [field]: value };
    onChange(updated);
  };

  const removeSlide = (index) => {
    onChange(slides.filter((_, i) => i !== index));
  };

  return (
    <div className="space-y-3">
      {slides.map((slide, index) => (
        <Card key={index} className="p-4">
          <div className="space-y-3">
            <div className="flex items-center justify-between mb-2">
              <span className="font-medium text-sm">Slide {index + 1}</span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => removeSlide(index)}
                className="text-red-600"
              >
                <Trash2 size={16} />
              </Button>
            </div>
            <Input
              placeholder="Title"
              value={slide.title}
              onChange={(e) => updateSlide(index, 'title', e.target.value)}
            />
            <Input
              placeholder="Subtitle"
              value={slide.subtitle}
              onChange={(e) => updateSlide(index, 'subtitle', e.target.value)}
            />
            <ImageUpload
              value={slide.image}
              onChange={(url) => updateSlide(index, 'image', url)}
              label="Slide Image"
            />
            <div className="border-t pt-3 mt-3">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Slide Button (optional)
              </label>
              <div className="grid grid-cols-2 gap-2">
                <Input
                  placeholder="Button Text (e.g., Learn More)"
                  value={slide.button_text || ''}
                  onChange={(e) => updateSlide(index, 'button_text', e.target.value)}
                />
                <Input
                  placeholder="Button Link (e.g., /about or https://...)"
                  value={slide.button_link || ''}
                  onChange={(e) => updateSlide(index, 'button_link', e.target.value)}
                />
              </div>
            </div>
          </div>
        </Card>
      ))}
      <Button onClick={addSlide} variant="outline" size="sm" className="w-full">
        <Plus size={16} className="mr-2" />
        Add Slide
      </Button>
    </div>
  );
}

// CTA Strip Editor Component
function CTAStripEditor({ formData, updateData }) {
  const isStats = formData.data.stats !== undefined;

  if (isStats) {
    return (
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Stats (for Stats Section)
          </label>
          {(formData.data.stats || []).map((stat, index) => (
            <div key={index} className="grid grid-cols-2 gap-2 mb-2">
              <Input
                placeholder="Number (e.g., 40K+)"
                value={stat.number}
                onChange={(e) => {
                  const stats = [...(formData.data.stats || [])];
                  stats[index] = { ...stats[index], number: e.target.value };
                  updateData('stats', stats);
                }}
              />
              <Input
                placeholder="Label"
                value={stat.label}
                onChange={(e) => {
                  const stats = [...(formData.data.stats || [])];
                  stats[index] = { ...stats[index], label: e.target.value };
                  updateData('stats', stats);
                }}
              />
            </div>
          ))}
          <Button
            onClick={() => {
              const stats = [...(formData.data.stats || []), { number: '', label: '' }];
              updateData('stats', stats);
            }}
            variant="outline"
            size="sm"
            className="w-full mt-2"
          >
            <Plus size={16} className="mr-2" />
            Add Stat
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Section Title
        </label>
        <Input
          value={formData.data.section_title || ''}
          onChange={(e) => updateData('section_title', e.target.value)}
          placeholder="Stay in the Loop"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Section Description
        </label>
        <Textarea
          value={formData.data.section_description || ''}
          onChange={(e) => updateData('section_description', e.target.value)}
          placeholder="Get the latest updates..."
          rows={3}
        />
        <p className="text-xs text-gray-500 mt-1">Supports HTML: &lt;p&gt;, &lt;b&gt;, &lt;i&gt;, &lt;a&gt;, &lt;span&gt;, &lt;div&gt;, &lt;h1-h6&gt;, &lt;ul&gt;, &lt;ol&gt;, &lt;li&gt;</p>
      </div>
      <div className="flex items-center space-x-2">
        <input
          type="checkbox"
          checked={formData.data.show_form || false}
          onChange={(e) => updateData('show_form', e.target.checked)}
          className="rounded"
        />
        <label className="text-sm text-gray-700">Show newsletter form</label>
      </div>
    </div>
  );
}

// Feature Grid Editor Component  
function FeatureGridEditor({ formData, updateData }) {
  const hasFeatures = formData.data.features !== undefined;
  const hasArticles = formData.data.articles !== undefined;
  const hasMeetups = formData.data.meetups !== undefined;
  const hasImages = formData.data.images !== undefined;

  return (
    <Tabs defaultValue="general" className="w-full">
      <TabsList>
        <TabsTrigger value="general">General</TabsTrigger>
        <TabsTrigger value="content">Content</TabsTrigger>
      </TabsList>

      <TabsContent value="general" className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Section Title
          </label>
          <Input
            value={formData.data.section_title || ''}
            onChange={(e) => updateData('section_title', e.target.value)}
            placeholder="Latest Tech News"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Section Description
          </label>
          <Textarea
            value={formData.data.section_description || ''}
            onChange={(e) => updateData('section_description', e.target.value)}
            placeholder="Stay informed..."
            rows={3}
          />
          <p className="text-xs text-gray-500 mt-1">Supports HTML: &lt;p&gt;, &lt;b&gt;, &lt;i&gt;, &lt;a&gt;, &lt;span&gt;, &lt;div&gt;, &lt;h1-h6&gt;, &lt;ul&gt;, &lt;ol&gt;, &lt;li&gt;</p>
        </div>
        {hasFeatures && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Layout
            </label>
            <Select
              value={formData.data.layout || 'grid'}
              onValueChange={(value) => updateData('layout', value)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="grid">Grid</SelectItem>
                <SelectItem value="side_by_side">Side by Side</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}
      </TabsContent>

      <TabsContent value="content" className="space-y-4">
        {hasFeatures && <FeaturesEditor formData={formData} updateData={updateData} />}
        {hasArticles && <ArticlesEditor formData={formData} updateData={updateData} />}
        {hasMeetups && <MeetupsEditor formData={formData} updateData={updateData} />}
        {hasImages && <ImagesEditor formData={formData} updateData={updateData} />}
        {!hasFeatures && !hasArticles && !hasMeetups && !hasImages && (
          <div className="text-center py-8 text-gray-500">
            <p>Select content type to edit:</p>
            <div className="flex gap-2 justify-center mt-4">
              <Button
                onClick={() => updateData('features', [])}
                variant="outline"
                size="sm"
              >
                Features
              </Button>
              <Button
                onClick={() => updateData('articles', [])}
                variant="outline"
                size="sm"
              >
                Articles
              </Button>
              <Button
                onClick={() => updateData('meetups', [])}
                variant="outline"
                size="sm"
              >
                Meetups
              </Button>
              <Button
                onClick={() => updateData('images', [])}
                variant="outline"
                size="sm"
              >
                Gallery
              </Button>
            </div>
          </div>
        )}
      </TabsContent>
    </Tabs>
  );
}

function FeaturesEditor({ formData, updateData }) {
  const features = formData.data.features || [];

  const addFeature = () => {
    updateData('features', [...features, { 
      icon: 'calendar', 
      title: '', 
      subtitle: '',
      description: '',
      date: '',
      image: '',
      features: [],
      cta_label: 'Learn More',
      cta_link: ''
    }]);
  };

  const updateFeature = (index, field, value) => {
    const updated = [...features];
    updated[index] = { ...updated[index], [field]: value };
    updateData('features', updated);
  };

  const removeFeature = (index) => {
    updateData('features', features.filter((_, i) => i !== index));
  };

  const addBulletPoint = (featureIndex) => {
    const updated = [...features];
    const currentBullets = updated[featureIndex].features || [];
    updated[featureIndex].features = [...currentBullets, ''];
    updateData('features', updated);
  };

  const updateBulletPoint = (featureIndex, bulletIndex, value) => {
    const updated = [...features];
    updated[featureIndex].features[bulletIndex] = value;
    updateData('features', updated);
  };

  const removeBulletPoint = (featureIndex, bulletIndex) => {
    const updated = [...features];
    updated[featureIndex].features = updated[featureIndex].features.filter((_, i) => i !== bulletIndex);
    updateData('features', updated);
  };

  return (
    <div className="space-y-3 max-h-[600px] overflow-y-auto">
      {features.map((feature, index) => (
        <Card key={index} className="p-4">
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="font-medium text-sm">Feature {index + 1}</span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => removeFeature(index)}
                className="text-red-600"
              >
                <Trash2 size={16} />
              </Button>
            </div>
            <Input
              placeholder="Title"
              value={feature.title}
              onChange={(e) => updateFeature(index, 'title', e.target.value)}
            />
            <Input
              placeholder="Subtitle / Label (e.g., TECH4LIFE Edition)"
              value={feature.subtitle || ''}
              onChange={(e) => updateFeature(index, 'subtitle', e.target.value)}
            />
            <Input
              placeholder="Date (e.g., Feb 11, 2026 or Spring 2026)"
              value={feature.date || ''}
              onChange={(e) => updateFeature(index, 'date', e.target.value)}
            />
            <Textarea
              placeholder="Description"
              value={feature.description}
              onChange={(e) => updateFeature(index, 'description', e.target.value)}
              rows={3}
            />
            
            {/* Feature Image */}
            <ImageUpload
              value={feature.image || ''}
              onChange={(url) => updateFeature(index, 'image', url)}
              label="Feature Image"
            />
            
            {/* Bullet Points */}
            <div className="border-t pt-3 mt-3">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Bullet Points / Highlights
              </label>
              <div className="space-y-2">
                {(feature.features || []).map((bullet, bulletIndex) => (
                  <div key={bulletIndex} className="flex gap-2">
                    <Input
                      placeholder="Bullet point text..."
                      value={bullet}
                      onChange={(e) => updateBulletPoint(index, bulletIndex, e.target.value)}
                      className="flex-1"
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeBulletPoint(index, bulletIndex)}
                      className="text-red-600"
                    >
                      <X size={16} />
                    </Button>
                  </div>
                ))}
                <Button
                  onClick={() => addBulletPoint(index)}
                  variant="outline"
                  size="sm"
                  className="w-full"
                >
                  <Plus size={14} className="mr-1" />
                  Add Bullet Point
                </Button>
              </div>
            </div>
            
            {/* CTA Button */}
            <div className="border-t pt-3 mt-3">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                CTA Button
              </label>
              <div className="grid grid-cols-2 gap-2">
                <Input
                  placeholder="Button Text (e.g., Learn More)"
                  value={feature.cta_label || ''}
                  onChange={(e) => updateFeature(index, 'cta_label', e.target.value)}
                />
                <Input
                  placeholder="Button Link (e.g., /expo-2026)"
                  value={feature.cta_link || ''}
                  onChange={(e) => updateFeature(index, 'cta_link', e.target.value)}
                />
              </div>
            </div>
          </div>
        </Card>
      ))}
      <Button onClick={addFeature} variant="outline" size="sm" className="w-full">
        <Plus size={16} className="mr-2" />
        Add Feature
      </Button>
    </div>
  );
}

function ArticlesEditor({ formData, updateData }) {
  const articles = formData.data.articles || [];

  const addArticle = () => {
    updateData('articles', [
      ...articles,
      { id: articles.length + 1, title: '', excerpt: '', category: 'News', image: '', date: '' },
    ]);
  };

  const updateArticle = (index, field, value) => {
    const updated = [...articles];
    updated[index] = { ...updated[index], [field]: value };
    updateData('articles', updated);
  };

  const removeArticle = (index) => {
    updateData('articles', articles.filter((_, i) => i !== index));
  };

  return (
    <div className="space-y-3 max-h-96 overflow-y-auto">
      {articles.map((article, index) => (
        <Card key={index} className="p-4">
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="font-medium text-sm">Article {index + 1}</span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => removeArticle(index)}
                className="text-red-600"
              >
                <Trash2 size={16} />
              </Button>
            </div>
            <Input
              placeholder="Title"
              value={article.title}
              onChange={(e) => updateArticle(index, 'title', e.target.value)}
            />
            <Textarea
              placeholder="Excerpt"
              value={article.excerpt}
              onChange={(e) => updateArticle(index, 'excerpt', e.target.value)}
              rows={2}
            />
            <ImageUpload
              value={article.image}
              onChange={(url) => updateArticle(index, 'image', url)}
              label="Article Image"
            />
            <Input
              placeholder="Date (e.g., Dec 15, 2025)"
              value={article.date}
              onChange={(e) => updateArticle(index, 'date', e.target.value)}
            />
          </div>
        </Card>
      ))}
      <Button onClick={addArticle} variant="outline" size="sm" className="w-full">
        <Plus size={16} className="mr-2" />
        Add Article
      </Button>
    </div>
  );
}

// News Section Editor - uses dynamic blog data
function NewsEditor({ formData, updateData }) {
  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Section Title
        </label>
        <Input
          value={formData.data.title || 'Latest Tech News'}
          onChange={(e) => updateData('title', e.target.value)}
          placeholder="Latest Tech News"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Section Subtitle
        </label>
        <Textarea
          value={formData.data.subtitle || "Stay informed with compelling stories from NYC's tech scene"}
          onChange={(e) => updateData('subtitle', e.target.value)}
          placeholder="Stay informed with compelling stories..."
          rows={2}
        />
      </div>
      <div className="border-t pt-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Number of Posts to Display
        </label>
        <Select
          value={String(formData.data.posts_count || 6)}
          onValueChange={(value) => updateData('posts_count', parseInt(value))}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="3">3 posts</SelectItem>
            <SelectItem value="6">6 posts (Default)</SelectItem>
            <SelectItem value="9">9 posts</SelectItem>
            <SelectItem value="12">12 posts</SelectItem>
          </SelectContent>
        </Select>
        <p className="text-xs text-gray-500 mt-1">Posts are automatically pulled from your published blog posts</p>
      </div>
      <div className="border-t pt-4 space-y-3">
        <h4 className="font-medium text-sm text-gray-700">Button Settings</h4>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Button Text
          </label>
          <Input
            value={formData.data.button_text || 'View All News'}
            onChange={(e) => updateData('button_text', e.target.value)}
            placeholder="View All News"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Button Link
          </label>
          <Input
            value={formData.data.button_link || '/blog/'}
            onChange={(e) => updateData('button_link', e.target.value)}
            placeholder="/blog/"
          />
        </div>
      </div>
    </div>
  );
}


function MeetupsEditor({ formData, updateData }) {
  const meetups = formData.data.meetups || [];

  const addMeetup = () => {
    updateData('meetups', [
      ...meetups,
      { id: meetups.length + 1, name: '', members: '', description: '', link: '', button_text: 'Join Group' },
    ]);
  };

  const updateMeetup = (index, field, value) => {
    const updated = [...meetups];
    updated[index] = { ...updated[index], [field]: value };
    updateData('meetups', updated);
  };

  const removeMeetup = (index) => {
    updateData('meetups', meetups.filter((_, i) => i !== index));
  };

  return (
    <div className="space-y-3">
      {meetups.map((meetup, index) => (
        <Card key={index} className="p-4">
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="font-medium text-sm">Meetup {index + 1}</span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => removeMeetup(index)}
                className="text-red-600"
              >
                <Trash2 size={16} />
              </Button>
            </div>
            <Input
              placeholder="Name"
              value={meetup.name}
              onChange={(e) => updateMeetup(index, 'name', e.target.value)}
            />
            <Input
              placeholder="Members (e.g., 2.4K members)"
              value={meetup.members}
              onChange={(e) => updateMeetup(index, 'members', e.target.value)}
            />
            <Textarea
              placeholder="Description"
              value={meetup.description}
              onChange={(e) => updateMeetup(index, 'description', e.target.value)}
              rows={3}
            />
            <div className="border-t pt-3 mt-3">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Button
              </label>
              <div className="grid grid-cols-2 gap-2">
                <Input
                  placeholder="Button Text (e.g., Join Group)"
                  value={meetup.button_text || ''}
                  onChange={(e) => updateMeetup(index, 'button_text', e.target.value)}
                />
                <Input
                  placeholder="Button Link URL"
                  value={meetup.link}
                  onChange={(e) => updateMeetup(index, 'link', e.target.value)}
                />
              </div>
            </div>
          </div>
        </Card>
      ))}
      <Button onClick={addMeetup} variant="outline" size="sm" className="w-full">
        <Plus size={16} className="mr-2" />
        Add Meetup
      </Button>
    </div>
  );
}

// Sortable Gallery Image for Homepage
function SortableHomepageImage({ id, image, index, onRemove }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes} className="relative group">
      <div className="aspect-square rounded-lg overflow-hidden border-2 border-gray-200 bg-gray-100">
        <img
          src={image}
          alt={`Gallery ${index + 1}`}
          className="w-full h-full object-cover"
        />
      </div>
      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all rounded-lg flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
        <div {...listeners} className="cursor-move bg-white/90 rounded-full p-2">
          <GripVertical size={16} className="text-gray-600" />
        </div>
        <button
          onClick={() => onRemove(index)}
          className="bg-red-500 hover:bg-red-600 text-white rounded-full p-2"
        >
          <Trash2 size={16} />
        </button>
      </div>
      <div className="absolute bottom-1 left-1 bg-black/60 text-white text-xs px-2 py-1 rounded">
        {index + 1}
      </div>
    </div>
  );
}

function ImagesEditor({ formData, updateData }) {
  const [uploading, setUploading] = React.useState(false);
  const fileInputRef = React.useRef(null);
  const { toast } = useToast();
  const images = formData.data.images || [];

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      const oldIndex = images.findIndex((_, i) => `home-img-${i}` === active.id);
      const newIndex = images.findIndex((_, i) => `home-img-${i}` === over.id);
      updateData('images', arrayMove(images, oldIndex, newIndex));
    }
  };

  const handleBulkUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    const validFiles = files.filter(file => {
      if (file.size > 5 * 1024 * 1024) {
        toast({ title: 'File too large', description: `${file.name} exceeds 5MB limit`, variant: 'destructive' });
        return false;
      }
      if (!file.type.startsWith('image/')) {
        toast({ title: 'Invalid file', description: `${file.name} is not an image`, variant: 'destructive' });
        return false;
      }
      return true;
    });

    if (validFiles.length === 0) return;

    setUploading(true);
    const uploadedUrls = [];

    for (const file of validFiles) {
      try {
        const formDataUpload = new FormData();
        formDataUpload.append('file', file);
        const response = await api.post('/admin/upload', formDataUpload, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        uploadedUrls.push(response.data.url);
      } catch (error) {
        console.error('Upload error:', error);
        toast({ title: 'Upload failed', description: `Failed to upload ${file.name}`, variant: 'destructive' });
      }
    }

    if (uploadedUrls.length > 0) {
      updateData('images', [...images, ...uploadedUrls]);
      toast({ title: 'Success', description: `Uploaded ${uploadedUrls.length} image(s)` });
    }

    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSingleUpload = (url) => {
    if (url) {
      updateData('images', [...images, url]);
    }
  };

  const removeImage = async (index) => {
    const imageUrl = images[index];
    // Try to delete from S3 if it's an S3 URL
    if (imageUrl && imageUrl.includes('s3') && imageUrl.includes('amazonaws.com')) {
      try {
        await api.delete('/admin/upload', { data: { url: imageUrl } });
      } catch (error) {
        console.error('S3 delete error:', error);
        // Continue with removal even if S3 delete fails
      }
    }
    updateData('images', images.filter((_, i) => i !== index));
  };

  return (
    <div className="space-y-4">
      {/* Bulk Upload Section */}
      <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          onChange={handleBulkUpload}
          className="hidden"
          disabled={uploading}
        />
        <div className="text-center">
          <Upload className="mx-auto h-8 w-8 text-gray-400" />
          <div className="mt-2 flex flex-col items-center gap-2">
            <Button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              variant="outline"
              size="sm"
            >
              <ImageIcon size={16} className="mr-2" />
              {uploading ? 'Uploading...' : 'Bulk Upload Images'}
            </Button>
            <p className="text-xs text-gray-500">
              Select multiple images (PNG, JPG, WebP up to 5MB each)
            </p>
          </div>
        </div>
      </div>

      {/* Single Image Upload */}
      <ImageUpload
        value=""
        onChange={handleSingleUpload}
        label="Or Add Single Image"
        showPreview={false}
      />

      {/* Image Gallery with Drag & Drop Reorder */}
      {images.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="block text-sm font-medium text-gray-700">
              Gallery Images ({images.length})
            </label>
            <p className="text-xs text-gray-500">Drag to reorder</p>
          </div>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={images.map((_, i) => `home-img-${i}`)}
              strategy={verticalListSortingStrategy}
            >
              <div className="grid grid-cols-3 gap-2 max-h-64 overflow-y-auto p-1">
                {images.map((image, index) => (
                  <SortableHomepageImage
                    key={`home-img-${index}`}
                    id={`home-img-${index}`}
                    image={image}
                    index={index}
                    onRemove={removeImage}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        </div>
      )}
    </div>
  );
}

function SortableSection({ section, onEdit, onDelete, onToggle }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id: section.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes}>
      <Card className="p-4 mb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4 flex-1">
            <div {...listeners} className="cursor-move">
              <GripVertical className="text-gray-400" size={20} />
            </div>
            <div className="flex-1">
              <div className="flex items-center space-x-2">
                <h3 className="font-medium text-gray-900">
                  {section.title || `${section.type} Section`}
                </h3>
                <span className="px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded">
                  {section.type}
                </span>
              </div>
              <p className="text-sm text-gray-500 mt-1">
                Order: {section.order}
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <Button
              onClick={() => onToggle(section.id, !section.is_enabled)}
              variant="ghost"
              size="sm"
              title={section.is_enabled ? 'Hide' : 'Show'}
            >
              {section.is_enabled ? (
                <Eye size={18} className="text-green-600" />
              ) : (
                <EyeOff size={18} className="text-gray-400" />
              )}
            </Button>
            <Button
              onClick={() => onEdit(section)}
              variant="ghost"
              size="sm"
            >
              <Edit size={18} />
            </Button>
            <Button
              onClick={() => onDelete(section.id)}
              variant="ghost"
              size="sm"
              className="text-red-600 hover:text-red-700"
            >
              <Trash2 size={18} />
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}

function SectionEditor({ section, onSave, onCancel }) {
  const [formData, setFormData] = useState({
    type: section?.type || 'hero',
    title: section?.title || '',
    anchor: section?.anchor || '',
    data: section?.data || {},
  });

  const handleSave = () => {
    onSave(formData);
  };

  const updateData = (key, value) => {
    setFormData(prev => ({
      ...prev,
      data: { ...prev.data, [key]: value }
    }));
  };

  // Common anchor field for all section types
  const anchorField = (
    <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-100">
      <label className="block text-sm font-medium text-gray-700 mb-1">Section Anchor (ID)</label>
      <Input
        value={formData.anchor || ''}
        onChange={(e) => setFormData(prev => ({ ...prev, anchor: e.target.value.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '') }))}
        placeholder="e.g., about, features, gallery"
        className="font-mono text-sm"
      />
      <p className="text-xs text-gray-500 mt-1">Used for menu links (e.g., #about). Letters, numbers, and hyphens only.</p>
    </div>
  );

  const renderFields = () => {
    switch (formData.type) {
      case 'hero':
        return (
          <>
            {anchorField}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Headline
              </label>
              <Input
                value={formData.data.headline || ''}
                onChange={(e) => updateData('headline', e.target.value)}
                placeholder="NYC's Premier Tech Community"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Subheadline
              </label>
              <Textarea
                value={formData.data.subheadline || ''}
                onChange={(e) => updateData('subheadline', e.target.value)}
                placeholder="Connecting innovators..."
                rows={3}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Primary CTA Label
                </label>
                <Input
                  value={formData.data.primary_cta_label || ''}
                  onChange={(e) => updateData('primary_cta_label', e.target.value)}
                  placeholder="Get Started"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Primary CTA Link
                </label>
                <Input
                  value={formData.data.primary_cta_link || ''}
                  onChange={(e) => updateData('primary_cta_link', e.target.value)}
                  placeholder="#newsletter"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Secondary CTA Label
                </label>
                <Input
                  value={formData.data.secondary_cta_label || ''}
                  onChange={(e) => updateData('secondary_cta_label', e.target.value)}
                  placeholder="Learn More"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Secondary CTA Link
                </label>
                <Input
                  value={formData.data.secondary_cta_link || ''}
                  onChange={(e) => updateData('secondary_cta_link', e.target.value)}
                  placeholder="#expos"
                />
              </div>
            </div>
            
            <div className="border-t pt-4 mt-4">
              <h3 className="font-medium text-gray-900 mb-3">Carousel Slides</h3>
              <SlidesEditor
                slides={formData.data.slides || []}
                onChange={(slides) => updateData('slides', slides)}
              />
            </div>
          </>
        );
      
      case 'feature_grid':
        return (
          <>
            {anchorField}
            <FeatureGridEditor formData={formData} updateData={updateData} />
          </>
        );
      
      case 'news':
        return (
          <>
            {anchorField}
            <NewsEditor formData={formData} updateData={updateData} />
          </>
        );
      
      case 'text':
        return (
          <>
            {anchorField}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Content
              </label>
              <Textarea
                value={formData.data.content || ''}
                onChange={(e) => updateData('content', e.target.value)}
                placeholder="Your text content here..."
                rows={8}
              />
              <p className="text-xs text-gray-500 mt-1">Supports HTML: &lt;h1&gt;-&lt;h6&gt;, &lt;p&gt;, &lt;a&gt;, &lt;b&gt;, &lt;i&gt;, &lt;span&gt;, &lt;div&gt;, &lt;ul&gt;, &lt;ol&gt;, &lt;li&gt;, &lt;hr&gt;</p>
            </div>
            <div className="border-t pt-4 mt-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">CTA Label (optional)</label>
                <Input
                  value={formData.data.cta_label || ''}
                  onChange={(e) => updateData('cta_label', e.target.value)}
                  placeholder="Learn More"
                />
              </div>
              <div className="mt-3">
                <label className="block text-sm font-medium text-gray-700 mb-2">CTA Link (optional)</label>
                <Input
                  value={formData.data.cta_link || ''}
                  onChange={(e) => updateData('cta_link', e.target.value)}
                  placeholder="#section or https://..."
                />
                <p className="text-xs text-gray-500 mt-1">Button only shows if both label and link are provided</p>
              </div>
            </div>
          </>
        );
      
      case 'cta_strip':
        return (
          <>
            {anchorField}
            <CTAStripEditor formData={formData} updateData={updateData} />
          </>
        );
      
      case 'slideshow':
        return (
          <>
            {anchorField}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Section Title
              </label>
              <Input
                value={formData.data.section_title || ''}
                onChange={(e) => updateData('section_title', e.target.value)}
                placeholder="Our Work"
              />
            </div>
            <div className="border-t pt-4 mt-4">
              <h3 className="font-medium text-gray-900 mb-3">Slides</h3>
              <SlidesEditor
                slides={formData.data.slides || []}
                onChange={(slides) => updateData('slides', slides)}
              />
            </div>
          </>
        );
      
      case 'image_text':
        return (
          <>
            {anchorField}
            <ImageUpload
              value={formData.data.image || ''}
              onChange={(url) => updateData('image', url)}
              label="Image"
            />
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Image Position
              </label>
              <Select
                value={formData.data.image_position || 'left'}
                onValueChange={(value) => updateData('image_position', value)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="left">Left</SelectItem>
                  <SelectItem value="right">Right</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Heading
              </label>
              <Input
                value={formData.data.heading || ''}
                onChange={(e) => updateData('heading', e.target.value)}
                placeholder="About Our Mission"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Content
              </label>
              <Textarea
                value={formData.data.content || ''}
                onChange={(e) => updateData('content', e.target.value)}
                placeholder="Your content here..."
                rows={6}
              />
              <p className="text-xs text-gray-500 mt-1">Supports HTML: &lt;p&gt;, &lt;b&gt;, &lt;i&gt;, &lt;a&gt;, &lt;span&gt;, &lt;div&gt;, &lt;h1-h6&gt;, &lt;ul&gt;, &lt;ol&gt;, &lt;li&gt;</p>
            </div>
            <div className="border-t pt-4 mt-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">CTA Label (optional)</label>
                <Input
                  value={formData.data.cta_label || ''}
                  onChange={(e) => updateData('cta_label', e.target.value)}
                  placeholder="Learn More"
                />
              </div>
              <div className="mt-3">
                <label className="block text-sm font-medium text-gray-700 mb-2">CTA Link (optional)</label>
                <Input
                  value={formData.data.cta_link || ''}
                  onChange={(e) => updateData('cta_link', e.target.value)}
                  placeholder="#section or https://..."
                />
                <p className="text-xs text-gray-500 mt-1">Button only shows if both label and link are provided</p>
              </div>
            </div>
          </>
        );
      
      case 'video_text':
        return (
          <>
            {anchorField}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Video Source</label>
              <Tabs 
                value={formData.data.video_source || 'embed'} 
                onValueChange={(value) => updateData('video_source', value)}
                className="w-full"
              >
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="embed">Embed Code</TabsTrigger>
                  <TabsTrigger value="upload">Upload Video</TabsTrigger>
                </TabsList>
                <TabsContent value="embed" className="mt-3">
                  <Textarea
                    value={formData.data.video_embed || ''}
                    onChange={(e) => updateData('video_embed', e.target.value)}
                    rows={4}
                    placeholder='<iframe src="https://www.youtube.com/embed/..." frameborder="0" allowfullscreen></iframe>'
                  />
                  <p className="text-xs text-gray-500 mt-1">Paste YouTube or Vimeo embed code here</p>
                </TabsContent>
                <TabsContent value="upload" className="mt-3">
                  <ImageUpload
                    value={formData.data.video_url || ''}
                    onChange={(url) => updateData('video_url', url)}
                    label="Video File"
                    accept="video/*"
                  />
                  <p className="text-xs text-gray-500 mt-1">Upload MP4, WebM, or other video formats</p>
                </TabsContent>
              </Tabs>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Video Position</label>
              <Select
                value={formData.data.video_position || 'left'}
                onValueChange={(value) => updateData('video_position', value)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="left">Left</SelectItem>
                  <SelectItem value="right">Right</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Heading</label>
              <Input
                value={formData.data.heading || ''}
                onChange={(e) => updateData('heading', e.target.value)}
                placeholder="Section Heading"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Subheading</label>
              <Input
                value={formData.data.subheading || ''}
                onChange={(e) => updateData('subheading', e.target.value)}
                placeholder="Optional subheading"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Content</label>
              <Textarea
                value={formData.data.content || ''}
                onChange={(e) => updateData('content', e.target.value)}
                placeholder="Your content here..."
                rows={6}
              />
              <p className="text-xs text-gray-500 mt-1">Supports HTML: &lt;p&gt;, &lt;b&gt;, &lt;i&gt;, &lt;a&gt;, &lt;span&gt;, &lt;div&gt;, &lt;h1-h6&gt;, &lt;ul&gt;, &lt;ol&gt;, &lt;li&gt;</p>
            </div>
            <div className="border-t pt-4 mt-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">CTA Label (optional)</label>
                <Input
                  value={formData.data.cta_label || ''}
                  onChange={(e) => updateData('cta_label', e.target.value)}
                  placeholder="Learn More"
                />
              </div>
              <div className="mt-3">
                <label className="block text-sm font-medium text-gray-700 mb-2">CTA Link (optional)</label>
                <Input
                  value={formData.data.cta_link || ''}
                  onChange={(e) => updateData('cta_link', e.target.value)}
                  placeholder="#section or https://..."
                />
                <p className="text-xs text-gray-500 mt-1">Button only shows if both label and link are provided</p>
              </div>
            </div>
          </>
        );
      
      case 'embed':
        return (
          <>
            {anchorField}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Section Title
                </label>
                <Input
                  value={formData.data.section_title || ''}
                  onChange={(e) => updateData('section_title', e.target.value)}
                  placeholder="Contact Us"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Section Subtitle
                </label>
                <Input
                  value={formData.data.section_subtitle || ''}
                  onChange={(e) => updateData('section_subtitle', e.target.value)}
                  placeholder="Get in touch with our team"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Section Description
                </label>
                <Textarea
                  value={formData.data.section_description || ''}
                  onChange={(e) => updateData('section_description', e.target.value)}
                  placeholder="Fill out the form below and we'll get back to you within 24 hours."
                  rows={3}
                />
                <p className="text-xs text-gray-500 mt-1">Supports HTML: &lt;p&gt;, &lt;b&gt;, &lt;i&gt;, &lt;a&gt;, &lt;span&gt;, &lt;div&gt;, &lt;h1-h6&gt;, &lt;ul&gt;, &lt;ol&gt;, &lt;li&gt;</p>
              </div>
            </div>
            <div className="border-t pt-4 mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Embed Code
              </label>
              <Textarea
                value={formData.data.embed_code || ''}
                onChange={(e) => updateData('embed_code', e.target.value)}
                placeholder={`<!-- Examples: -->
<iframe src="https://www.youtube.com/embed/..." frameborder="0" allowfullscreen></iframe>

<!-- Or JavaScript embeds like Close CRM forms: -->
<script src="https://webforms.closeiocdn.com/webforms.js" type="module" crossorigin="anonymous" defer></script>
<close-form id="form_xxx"></close-form>`}
                rows={8}
                className="font-mono text-sm"
              />
              <p className="text-sm text-gray-500 mt-2">
                Paste any embed code: YouTube/Vimeo iframes, JavaScript widgets, form embeds (Close CRM, HubSpot, etc.), or custom HTML/JS
              </p>
            </div>
            <div className="border-t pt-4 mt-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Embed Width (Desktop)
                </label>
                <Select
                  value={formData.data.embed_width || '60'}
                  onValueChange={(value) => updateData('embed_width', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select width" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="40">40%</SelectItem>
                    <SelectItem value="50">50%</SelectItem>
                    <SelectItem value="60">60% (Default)</SelectItem>
                    <SelectItem value="70">70%</SelectItem>
                    <SelectItem value="80">80%</SelectItem>
                    <SelectItem value="90">90%</SelectItem>
                    <SelectItem value="100">100% (Full Width)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500 mt-1">Width on desktop screens. Always full width on mobile.</p>
              </div>
            </div>
            <div className="border-t pt-4 mt-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">CTA Label (optional)</label>
                <Input
                  value={formData.data.cta_label || ''}
                  onChange={(e) => updateData('cta_label', e.target.value)}
                  placeholder="Learn More"
                />
              </div>
              <div className="mt-3">
                <label className="block text-sm font-medium text-gray-700 mb-2">CTA Link (optional)</label>
                <Input
                  value={formData.data.cta_link || ''}
                  onChange={(e) => updateData('cta_link', e.target.value)}
                  placeholder="#section or https://..."
                />
                <p className="text-xs text-gray-500 mt-1">Button only shows if both label and link are provided</p>
              </div>
            </div>
          </>
        );
      
      case 'gallery':
        return (
          <>
            {anchorField}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Section Title</label>
                <Input
                  value={formData.data.section_title || ''}
                  onChange={(e) => updateData('section_title', e.target.value)}
                  placeholder="Photo Gallery"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Section Description</label>
                <Textarea
                  value={formData.data.section_description || ''}
                  onChange={(e) => updateData('section_description', e.target.value)}
                  placeholder="Highlights from our events"
                  rows={2}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Images (one URL per line)</label>
                <Textarea
                  value={(formData.data.images || []).join('\n')}
                  onChange={(e) => updateData('images', e.target.value.split('\n').filter(url => url.trim()))}
                  placeholder="https://example.com/image1.jpg&#10;https://example.com/image2.jpg"
                  rows={6}
                />
              </div>
              <div className="border-t pt-4 mt-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">CTA Label (optional)</label>
                  <Input
                    value={formData.data.cta_label || ''}
                    onChange={(e) => updateData('cta_label', e.target.value)}
                    placeholder="View All Photos"
                  />
                </div>
                <div className="mt-3">
                  <label className="block text-sm font-medium text-gray-700 mb-2">CTA Link (optional)</label>
                  <Input
                    value={formData.data.cta_link || ''}
                    onChange={(e) => updateData('cta_link', e.target.value)}
                    placeholder="#section or https://..."
                  />
                  <p className="text-xs text-gray-500 mt-1">Button only shows if both label and link are provided</p>
                </div>
              </div>
            </div>
          </>
        );

      case 'sponsors':
        return (
          <>
            {anchorField}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Section Title</label>
                <Input
                  value={formData.data.section_title || ''}
                  onChange={(e) => updateData('section_title', e.target.value)}
                  placeholder="Our Sponsors"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Section Description</label>
                <Textarea
                  value={formData.data.section_description || ''}
                  onChange={(e) => updateData('section_description', e.target.value)}
                  placeholder="Thank you to our partners"
                  rows={2}
                />
              </div>
              <p className="text-sm text-gray-500">For detailed sponsor management with logos and links, use the Page Builder.</p>
              <div className="border-t pt-4 mt-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">CTA Label (optional)</label>
                  <Input
                    value={formData.data.cta_label || ''}
                    onChange={(e) => updateData('cta_label', e.target.value)}
                    placeholder="Become a Sponsor"
                  />
                </div>
                <div className="mt-3">
                  <label className="block text-sm font-medium text-gray-700 mb-2">CTA Link (optional)</label>
                  <Input
                    value={formData.data.cta_link || ''}
                    onChange={(e) => updateData('cta_link', e.target.value)}
                    placeholder="#section or https://..."
                  />
                  <p className="text-xs text-gray-500 mt-1">Button only shows if both label and link are provided</p>
                </div>
              </div>
            </div>
          </>
        );

      case 'press':
        return (
          <>
            {anchorField}
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Section Title</label>
                  <Input
                    value={formData.data.section_title || ''}
                    onChange={(e) => updateData('section_title', e.target.value)}
                    placeholder="In The Press"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Section Subtitle</label>
                  <Input
                    value={formData.data.section_subtitle || ''}
                    onChange={(e) => updateData('section_subtitle', e.target.value)}
                    placeholder="Latest Coverage"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Section Description</label>
                <Textarea
                  value={formData.data.section_description || ''}
                  onChange={(e) => updateData('section_description', e.target.value)}
                  placeholder="What the media is saying about us"
                  rows={2}
                />
              </div>
              <p className="text-sm text-gray-500">For detailed press quote management, use the Page Builder.</p>
              <div className="border-t pt-4 mt-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">CTA Label (optional)</label>
                  <Input
                    value={formData.data.cta_label || ''}
                    onChange={(e) => updateData('cta_label', e.target.value)}
                    placeholder="Read More Coverage"
                  />
                </div>
                <div className="mt-3">
                  <label className="block text-sm font-medium text-gray-700 mb-2">CTA Link (optional)</label>
                  <Input
                    value={formData.data.cta_link || ''}
                    onChange={(e) => updateData('cta_link', e.target.value)}
                    placeholder="#section or https://..."
                  />
                  <p className="text-xs text-gray-500 mt-1">Button only shows if both label and link are provided</p>
                </div>
              </div>
            </div>
          </>
        );

      case 'newsletter_flodesk':
        return (
          <>
            {anchorField}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Flodesk Form ID</label>
                <Input
                  value={formData.data.flodesk_form_id || '698ff949955577ffd7fda382'}
                  onChange={(e) => updateData('flodesk_form_id', e.target.value)}
                  placeholder="698ff949955577ffd7fda382"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Find your Form ID in Flodesk: Forms → Select Form → Share → Get the ID from the embed code
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Title (optional)</label>
                <Input
                  value={formData.data.section_title || ''}
                  onChange={(e) => updateData('section_title', e.target.value)}
                  placeholder="Leave empty to hide"
                />
                <p className="text-xs text-gray-500 mt-1">Shows above the Flodesk form. Leave empty to only use Flodesk's built-in title.</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Subtitle (optional)</label>
                <Input
                  value={formData.data.section_subtitle || ''}
                  onChange={(e) => updateData('section_subtitle', e.target.value)}
                  placeholder="Leave empty to hide"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Footer Text (with checkmark)</label>
                <Input
                  value={formData.data.bottom_text || 'Join 40,000+ subscribers already in our community'}
                  onChange={(e) => updateData('bottom_text', e.target.value)}
                  placeholder="Join 40,000+ subscribers already in our community"
                />
                <p className="text-xs text-gray-500 mt-1">Shows below the form with a green checkmark. Leave empty to hide.</p>
              </div>
            </div>
          </>
        );
      
      default:
        return (
          <div className="text-center py-8 text-gray-500">
            Section editor for {formData.type} coming soon...
          </div>
        );
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Section Type
        </label>
        <Select
          value={formData.type}
          onValueChange={(value) => setFormData(prev => ({ ...prev, type: value }))}
          disabled={!!section}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {SECTION_TYPES.map(type => (
              <SelectItem key={type.value} value={type.value}>
                {type.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Section Title (optional)
        </label>
        <Input
          value={formData.title}
          onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
          placeholder="Internal reference name"
        />
      </div>

      {renderFields()}

      <DialogFooter>
        <Button variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button onClick={handleSave} className="bg-black hover:bg-gray-800">
          <Save size={16} className="mr-2" />
          Save Section
        </Button>
      </DialogFooter>
    </div>
  );
}

const HomepageBuilder = () => {
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingSection, setEditingSection] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [homepageSettings, setHomepageSettings] = useState({
    meta_title: '',
    meta_description: '',
    og_image: '',
    menu_id: ''
  });
  const [menus, setMenus] = useState([]);
  const [savingSettings, setSavingSettings] = useState(false);
  const { toast } = useToast();

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    fetchSections();
    fetchHomepageSettings();
    fetchMenus();
  }, []);

  const fetchSections = async () => {
    try {
      const response = await api.get('/admin/homepage');
      setSections(response.data);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to fetch homepage sections',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchHomepageSettings = async () => {
    try {
      const response = await api.get('/admin/homepage/settings');
      setHomepageSettings({
        meta_title: response.data.meta_title || '',
        meta_description: response.data.meta_description || '',
        og_image: response.data.og_image || '',
        menu_id: response.data.menu_id || ''
      });
    } catch (error) {
      console.error('Failed to fetch homepage settings:', error);
    }
  };

  const fetchMenus = async () => {
    try {
      const response = await api.get('/menus');
      setMenus(response.data);
    } catch (error) {
      console.error('Failed to fetch menus:', error);
    }
  };

  const saveHomepageSettings = async () => {
    setSavingSettings(true);
    try {
      await api.put('/admin/homepage/settings', homepageSettings);
      toast({ title: 'Success', description: 'Homepage settings saved' });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to save homepage settings',
        variant: 'destructive',
      });
    } finally {
      setSavingSettings(false);
    }
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      setSections((items) => {
        const oldIndex = items.findIndex((i) => i.id === active.id);
        const newIndex = items.findIndex((i) => i.id === over.id);
        const newItems = arrayMove(items, oldIndex, newIndex);
        
        // Update order numbers
        const reordered = newItems.map((item, index) => ({
          ...item,
          order: index
        }));
        
        // Save to backend
        saveOrder(reordered);
        
        return reordered;
      });
    }
  };

  const saveOrder = async (reorderedSections) => {
    try {
      await api.post('/admin/homepage/reorder', {
        items: reorderedSections.map(s => ({ id: s.id, order: s.order }))
      });
      toast({ title: 'Success', description: 'Section order updated' });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to update section order',
        variant: 'destructive',
      });
    }
  };

  const handleCreate = () => {
    setEditingSection(null);
    setIsDialogOpen(true);
  };

  const handleEdit = (section) => {
    setEditingSection(section);
    setIsDialogOpen(true);
  };

  const handleSave = async (formData) => {
    try {
      if (editingSection) {
        await api.put(`/admin/homepage/${editingSection.id}`, formData);
        toast({ title: 'Success', description: 'Section updated' });
      } else {
        await api.post('/admin/homepage', { ...formData, order: sections.length, is_enabled: true });
        toast({ title: 'Success', description: 'Section created' });
      }
      setIsDialogOpen(false);
      fetchSections();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to save section',
        variant: 'destructive',
      });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this section?')) return;

    try {
      await api.delete(`/admin/homepage/${id}`);
      toast({ title: 'Success', description: 'Section deleted' });
      fetchSections();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to delete section',
        variant: 'destructive',
      });
    }
  };

  const handleToggle = async (id, isEnabled) => {
    try {
      await api.put(`/admin/homepage/${id}`, { is_enabled: isEnabled });
      toast({ 
        title: 'Success', 
        description: isEnabled ? 'Section enabled' : 'Section disabled' 
      });
      fetchSections();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to toggle section',
        variant: 'destructive',
      });
    }
  };

  if (loading) {
    return <div className="p-8">Loading...</div>;
  }

  return (
    <div className="p-8">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Homepage Builder</h1>
          <p className="text-gray-600 mt-2">
            Drag and drop to reorder sections • {sections.length} sections
          </p>
        </div>
        <Button onClick={handleCreate} className="bg-black hover:bg-gray-800">
          <Plus size={20} className="mr-2" />
          Add Section
        </Button>
      </div>

      {/* SEO & Menu Settings Card */}
      <Card className="p-6 mb-8">
        <div className="flex items-center gap-2 mb-6">
          <Search size={24} className="text-gray-700" />
          <h2 className="text-xl font-bold text-gray-900">SEO & Menu Settings</h2>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Meta Title (SEO)
              </label>
              <Input
                value={homepageSettings.meta_title}
                onChange={(e) => setHomepageSettings({ ...homepageSettings, meta_title: e.target.value })}
                placeholder="Pulse NYC | NYC's Premier Tech Community"
                data-testid="homepage-meta-title"
              />
              <p className="text-xs text-gray-500 mt-1">
                Appears in browser tab and search results
              </p>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Meta Description (SEO)
              </label>
              <Textarea
                value={homepageSettings.meta_description}
                onChange={(e) => setHomepageSettings({ ...homepageSettings, meta_description: e.target.value })}
                placeholder="NYC's Premier Tech Community - Connecting innovators, entrepreneurs, and tech enthusiasts."
                rows={3}
                data-testid="homepage-meta-description"
              />
              <p className="text-xs text-gray-500 mt-1">
                Appears in search results • Recommended: 150-160 characters
              </p>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Social Share Image (og:image)
              </label>
              <p className="text-xs text-gray-500 mb-2">
                Image shown when homepage is shared on Facebook, LinkedIn, Twitter. Recommended: 1200x630px
              </p>
              <ImageUpload
                value={homepageSettings.og_image}
                onChange={(url) => setHomepageSettings({ ...homepageSettings, og_image: url })}
                label="Social Share Image"
              />
            </div>
          </div>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <div className="flex items-center gap-2">
                  <Globe size={16} />
                  Menu Assignment
                </div>
              </label>
              <Select
                value={homepageSettings.menu_id || 'default'}
                onValueChange={(value) => setHomepageSettings({ 
                  ...homepageSettings, 
                  menu_id: value === 'default' ? '' : value 
                })}
              >
                <SelectTrigger data-testid="homepage-menu-select">
                  <SelectValue placeholder="Select menu" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="default">Use Default Menu</SelectItem>
                  {menus.map((menu) => (
                    <SelectItem key={menu.id} value={menu.id}>
                      {menu.name} {menu.is_default && '(Default)'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500 mt-1">
                Which navigation menu to display on the homepage
              </p>
            </div>
            
            <div className="pt-4">
              <Button 
                onClick={saveHomepageSettings} 
                disabled={savingSettings}
                className="bg-black hover:bg-gray-800"
              >
                <Save size={16} className="mr-2" />
                {savingSettings ? 'Saving...' : 'Save SEO Settings'}
              </Button>
            </div>
          </div>
        </div>
      </Card>

      {sections.length === 0 ? (
        <Card className="p-12 text-center">
          <p className="text-xl text-gray-600 mb-4">No sections yet</p>
          <Button onClick={handleCreate} className="bg-black hover:bg-gray-800">
            <Plus size={20} className="mr-2" />
            Create Your First Section
          </Button>
        </Card>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={sections.map(s => s.id)}
            strategy={verticalListSortingStrategy}
          >
            {sections.map((section) => (
              <SortableSection
                key={section.id}
                section={section}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onToggle={handleToggle}
              />
            ))}
          </SortableContext>
        </DndContext>
      )}

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingSection ? 'Edit Section' : 'Create New Section'}
            </DialogTitle>
          </DialogHeader>
          <SectionEditor
            section={editingSection}
            onSave={handleSave}
            onCancel={() => setIsDialogOpen(false)}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default HomepageBuilder;
